/**
 * THE APP'S ONE DIALOG (house rule, owner 2026-10-24) — browser dialogs (``window.confirm`` / ``alert`` / ``prompt``)
 * are BANNED: every question goes through this in-app dialog, the same look everywhere. Call it from anywhere:
 *
 *   if (!(await confirmDialog({ title: 'Delete snapshot?', message: 'This can’t be undone.', confirmLabel: 'Delete',
 *                               danger: true }))) return;
 *   await alertDialog({ title: 'Some rooms weren’t moved', message });
 *   const reason = await promptDialog({ title: 'Why?', placeholder: 'reason' });   // null = cancelled
 *
 * ``<DialogHost/>`` is mounted ONCE at the app root (ui-shell App); a guard test fails CI on any browser dialog.
 */
import React, { useEffect, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog.jsx';

let _show = null;           // the mounted host's opener
const _queue = [];          // asked before the host mounted

function open(kind, opts) {
  const o = typeof opts === 'string' ? { message: opts } : { ...(opts || {}) };
  return new Promise((resolve) => {
    const req = { kind, ...o, resolve };
    if (_show) _show(req); else _queue.push(req);
  });
}

/** → Promise<boolean> */
export const confirmDialog = (opts) => open('confirm', opts);
/** → Promise<void> */
export const alertDialog = (opts) => open('alert', opts);
/** → Promise<string|null> (null = cancelled) */
export const promptDialog = (opts) => open('prompt', opts);

export default function DialogHost() {
  const [cur, setCur] = useState(null);
  const [text, setText] = useState('');
  const pending = useRef([]);
  useEffect(() => {
    _show = (req) => {
      setCur((c) => {
        if (c) { pending.current.push(req); return c; }
        setText(req.defaultValue || '');
        return req;
      });
    };
    while (_queue.length) _show(_queue.shift());
    return () => { _show = null; };
  }, []);
  const done = (value) => {
    if (!cur) return;
    cur.resolve(value);
    const next = pending.current.shift() || null;
    setText(next?.defaultValue || '');
    setCur(next);
  };
  if (!cur) return null;
  const isAlert = cur.kind === 'alert';
  const isPrompt = cur.kind === 'prompt';
  const message = isPrompt ? (
    <>
      {cur.message ? <span className="block">{cur.message}</span> : null}
      <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={cur.placeholder || ''}
        data-testid="app-dialog-input" onKeyDown={(e) => { if (e.key === 'Enter') done(text); }}
        className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800" />
    </>
  ) : cur.message;
  return (
    <ConfirmDialog
      open
      testid="app-dialog"
      title={cur.title || (isAlert ? 'Heads up' : 'Are you sure?')}
      message={message}
      confirmLabel={cur.confirmLabel || (isAlert ? 'OK' : isPrompt ? 'Continue' : 'Confirm')}
      cancelLabel={isAlert ? null : (cur.cancelLabel || 'Cancel')}
      danger={!!cur.danger}
      onConfirm={() => done(isAlert ? undefined : isPrompt ? text : true)}
      onCancel={() => done(isAlert ? undefined : isPrompt ? null : false)}
    />
  );
}
